/**
 * Dead Links page — Settings → Dead Links
 *
 * Reports every currently-broken URL. Only dead/error URLs live here;
 * alive URLs are never written, and previously dead URLs are removed on
 * the next scan if they recover.
 */

import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    RefreshCw,
    Trash2,
    ExternalLink,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    getDeadLinks,
    clearAllDeadLinks,
    removeDeadLink,
    checkUrl,
    getUrlCollections,
    type DeadLink,
    type LinkStatus,
} from '@/api/link-check';

const STATUS_OPTIONS: LinkStatus[] = [
    'dead',
    'timeout',
    'server_error',
    'dns_error',
    'connection_error',
    'ssl_error',
    'redirect_loop',
    'invalid_url',
    'error',
];

const ALL = '__all__';
const PAGE_SIZE = 25;

export function DeadLinksPage() {
    const qc = useQueryClient();
    const { data: collectionOptions = [] } = useQuery({
        queryKey: ['link-check-url-collections'],
        queryFn: getUrlCollections,
    });
    const [collection, setCollection] = useState<string>(ALL);
    const [status, setStatus] = useState<string>(ALL);
    const [page, setPage] = useState(1);
    const [confirmClear, setConfirmClear] = useState(false);

    const queryKey = ['dead-links', { collection, status, page }];

    const { data, isFetching } = useQuery({
        queryKey,
        queryFn: () =>
            getDeadLinks({
                collection: collection === ALL ? undefined : collection,
                status: status === ALL ? undefined : (status as LinkStatus),
                page,
                limit: PAGE_SIZE,
            }),
    });

    const rows = data?.data ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const clearAll = useMutation({
        mutationFn: clearAllDeadLinks,
        onSuccess: (deleted) => {
            toast.success(`Cleared ${deleted} dead link${deleted === 1 ? '' : 's'}`);
            qc.invalidateQueries({ queryKey: ['dead-links'] });
        },
        onError: (err: any) => toast.error(err?.message ?? 'Failed to clear'),
    });

    const removeOne = useMutation({
        mutationFn: (id: string) => removeDeadLink(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['dead-links'] });
        },
        onError: (err: any) => toast.error(err?.message ?? 'Failed to remove'),
    });

    const recheck = useMutation({
        mutationFn: async (row: DeadLink) => {
            const result = await checkUrl(row.url);
            // If it's now alive, also remove the row.
            if (result.status === 'alive') {
                await removeDeadLink(row.id);
            }
            return result;
        },
        onSuccess: (result) => {
            if (result.status === 'alive') {
                toast.success('URL is alive — removed from report');
            } else {
                toast.info(`Still ${result.status}${result.statusCode ? ` (${result.statusCode})` : ''}`);
            }
            qc.invalidateQueries({ queryKey: ['dead-links'] });
        },
        onError: (err: any) => toast.error(err?.message ?? 'Re-check failed'),
    });

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle>Dead Links</CardTitle>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => qc.invalidateQueries({ queryKey: ['dead-links'] })}
                        disabled={isFetching}
                        className="gap-2"
                    >
                        <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                        Refresh
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmClear(true)}
                        disabled={total === 0 || clearAll.isPending}
                        className="gap-2"
                    >
                        <Trash2 className="h-4 w-4" />
                        Clear All
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">Collection</label>
                        <Select
                            value={collection}
                            onValueChange={(v) => {
                                setCollection(v);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-56">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All collections</SelectItem>
                                {collectionOptions.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">Status</label>
                        <Select
                            value={status}
                            onValueChange={(v) => {
                                setStatus(v);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-44">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All statuses</SelectItem>
                                {STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto text-sm text-muted-foreground">
                        {total} dead link{total === 1 ? '' : 's'}
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Collection</TableHead>
                            <TableHead>Item ID</TableHead>
                            <TableHead>Field</TableHead>
                            <TableHead>URL</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Last checked</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                                    {isFetching ? 'Loading…' : 'No dead links — everything looks healthy.'}
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell className="font-medium">{row.collection}</TableCell>
                                <TableCell className="font-mono text-xs">
                                    <a
                                        href={`/admin/content/${row.collection}/${encodeURIComponent(row.item_id)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                        title="Open item record in new tab"
                                    >
                                        {row.item_id}
                                    </a>
                                </TableCell>
                                <TableCell className="font-mono text-xs">{row.field}</TableCell>
                                <TableCell className="max-w-md truncate">
                                    <a
                                        href={row.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        {row.url}
                                    </a>
                                </TableCell>
                                <TableCell><StatusBadge status={row.status} code={row.status_code} /></TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {new Date(row.last_checked).toLocaleString()}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="inline-flex gap-1">
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            title="Open URL in new tab"
                                            asChild
                                        >
                                            <a href={row.url} target="_blank" rel="noopener noreferrer">
                                                <ExternalLink className="h-4 w-4" />
                                            </a>
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            title="Re-check"
                                            disabled={recheck.isPending}
                                            onClick={() => recheck.mutate(row)}
                                        >
                                            <RefreshCw className={recheck.isPending && recheck.variables?.id === row.id ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            title="Remove"
                                            className="text-destructive"
                                            onClick={() => removeOne.mutate(row.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {totalPages > 1 && (
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            Page {page} of {totalPages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                )}
            </CardContent>

            <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Clear all dead links?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This deletes every record from the dead-links table. Scan history is kept.
                            You can re-populate the table by running a new scan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => clearAll.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Clear All
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

function StatusBadge({ status, code }: { status: LinkStatus; code: number | null }) {
    const palette: Record<LinkStatus, string> = {
        alive: 'bg-green-100 text-green-800 border-green-200',
        dead: 'bg-red-100 text-red-800 border-red-200',
        server_error: 'bg-red-100 text-red-800 border-red-200',
        timeout: 'bg-orange-100 text-orange-800 border-orange-200',
        dns_error: 'bg-orange-100 text-orange-800 border-orange-200',
        connection_error: 'bg-orange-100 text-orange-800 border-orange-200',
        ssl_error: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        redirect_loop: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        invalid_url: 'bg-gray-100 text-gray-700 border-gray-200',
        error: 'bg-gray-100 text-gray-700 border-gray-200',
    };

    return (
        <Badge variant="outline" className={palette[status]}>
            {status}
            {code != null && ` (${code})`}
        </Badge>
    );
}

